/**
 * Wristband-service inter-service contracts consumed by kiosk-service.
 * Shapes mirror the implemented API, not aspirational admin schemas.
 */

import { z } from 'zod';

export const WristbandServiceEnvelopeSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean().optional(),
    data: dataSchema.optional(),
  });

// ============================================================================
// GET /wristbands/code/:code  +  GET /wristbands/:id
// ============================================================================

export const WristbandServiceDetailsSchema = z.object({
  id: z.string().min(1),
  wristbandCode: z.string().min(1),
  qrContent: z.string(),
  qrCodeBase64: z.string().optional(),
  hmacTruncated: z.string().optional(),
  status: z.string(),
  ticketId: z.string(),
  orderId: z.string(),
  customerId: z.string(),
  creditBalance: z.number().optional(),
  visitorName: z.string().nullable().optional(),
  issuedAt: z.union([z.string(), z.date()]).nullable().optional(),
  expiresAt: z.union([z.string(), z.date()]).optional(),
  lastScanAt: z.union([z.string(), z.date()]).nullable().optional(),
  lastScanLocation: z.string().nullable().optional(),
});

export type WristbandServiceDetails = z.infer<typeof WristbandServiceDetailsSchema>;

// ============================================================================
// POST /wristbands/:id/credits/add
// ============================================================================

export const WristbandAddCreditsRequestSchema = z.object({
  amount: z.number().int().positive(),
  source: z.enum(['purchase', 'topup', 'promo', 'refund', 'adjustment']),
  referenceType: z.string().max(50).optional(),
  referenceId: z.string().max(100).optional(),
  idempotencyKey: z.string().max(100),
  description: z.string().max(500).optional(),
});

export type WristbandAddCreditsRequest = z.infer<typeof WristbandAddCreditsRequestSchema>;

export const WristbandBalanceResponseSchema = z.object({
  wristbandId: z.string(),
  creditBalance: z.number(),
  todayAdded: z.number().optional(),
  todayUsed: z.number().optional(),
});

export type WristbandBalanceResponse = z.infer<typeof WristbandBalanceResponseSchema>;

// ============================================================================
// POST /wristbands/batch-issue
// ============================================================================

export const BatchIssueTicketItemSchema = z.object({
  ticketId: z.string().min(1),
  customerId: z.string().min(1),
  customerName: z.string().optional(),
  ticketType: z.string().optional(),
  visitDate: z.string().optional(),
  wristbandId: z.string().optional(),
  qrContent: z.string().optional(),
  hmacTruncated: z.string().optional(),
  timestampIssued: z.number().optional(),
});

export const BatchIssueWristbandsRequestSchema = z.object({
  orderId: z.string().min(1),
  tickets: z.array(BatchIssueTicketItemSchema).min(1),
  issuanceType: z.string().min(1),
  issuanceLocation: z.string().min(1),
  printerId: z.string().optional(),
  staffId: z.string().optional(),
});

export type BatchIssueWristbandsRequest = z.infer<typeof BatchIssueWristbandsRequestSchema>;

export const IssuedWristbandRecordSchema = z.object({
  id: z.string().min(1),
  wristbandCode: z.string().min(1),
  qrContent: z.string(),
  qrCodeBase64: z.string().optional(),
  hmacTruncated: z.string().optional(),
  ticketId: z.string(),
  status: z.string().optional(),
  printedAt: z.string().nullable().optional(),
});

export type IssuedWristbandRecord = z.infer<typeof IssuedWristbandRecordSchema>;

export const BatchIssueWristbandsResponseSchema = z.object({
  orderId: z.string().optional(),
  totalRequested: z.number().int().optional(),
  totalCreated: z.number().int().optional(),
  wristbands: z.array(IssuedWristbandRecordSchema).optional(),
  errors: z
    .array(
      z.object({
        ticketId: z.string(),
        error: z.string(),
      }),
    )
    .optional(),
});

export type BatchIssueWristbandsResponse = z.infer<typeof BatchIssueWristbandsResponseSchema>;

// ============================================================================
// GET /wristbands/orders/:orderId
// ============================================================================

export const WristbandsForOrderResponseSchema = z.object({
  orderId: z.string(),
  total: z.number().int(),
  wristbands: z.array(IssuedWristbandRecordSchema),
});

export type WristbandsForOrderResponse = z.infer<typeof WristbandsForOrderResponseSchema>;

// ============================================================================
// POST /wristbands/orders/:orderId/print-all  +  POST /wristbands/:id/print
// ============================================================================

export const PrintWristbandRequestSchema = z.object({
  kioskId: z.string().optional(),
});

export type PrintWristbandRequest = z.infer<typeof PrintWristbandRequestSchema>;

export const WristbandPrintJobResponseSchema = z.object({
  jobId: z.string(),
  status: z.string(),
  printData: z.record(z.string(), z.unknown()).optional(),
});

export type WristbandPrintJobResponse = z.infer<typeof WristbandPrintJobResponseSchema>;

export const WristbandPrintAllResultSchema = z.object({
  wristbandId: z.string(),
  wristbandCode: z.string(),
  qrContent: z.string(),
  jobId: z.string().nullable(),
  status: z.string(),
  printedAt: z.string().nullable(),
});

export type WristbandPrintAllResult = z.infer<typeof WristbandPrintAllResultSchema>;

export const WristbandPrintAllResponseSchema = z.object({
  orderId: z.string().optional(),
  total: z.number().int(),
  completed: z.number().int(),
  failed: z.number().int(),
  alreadyPrintedCount: z.number().int().optional(),
  alreadyPrinted: z.boolean().optional(),
  lastPrintedAt: z.string().nullable().optional(),
  results: z.array(WristbandPrintAllResultSchema),
});

export type WristbandPrintAllResponse = z.infer<typeof WristbandPrintAllResponseSchema>;

// ============================================================================
// GET /wristbands?qrContent=  (individual print lookup)
// ============================================================================

export const WristbandQrLookupSchema = z
  .object({
    id: z.string().min(1),
    wristbandCode: z.string(),
    qrContent: z.string(),
  })
  .passthrough();

export type WristbandQrLookup = z.infer<typeof WristbandQrLookupSchema>;
